import { createHash } from "node:crypto";
import {
	existsSync,
	readFileSync,
	readdirSync,
	rmSync,
	statSync,
	writeFileSync,
} from "node:fs";
import { basename, join, relative, sep } from "node:path";
import { fileURLToPath } from "node:url";
import { zipSync } from "fflate";
import { version as viteVersion } from "vite";
import type { HmrOptions, Plugin, UserConfig } from "vite";

/**
 * Whop hosting plugin — `import { whop } from "@whop/cli/vite"`.
 *
 * Framework-agnostic: after `vite build` produces the standard Cloudflare
 * layout (`dist/client` for static assets, `dist/server` for the SSR worker),
 * this packs them into `dist/whop-build.zip` — the archive `whop apps deploy`
 * uploads. Works with any Vite framework that builds for Cloudflare Workers
 * (TanStack Start, Nitro, Hono, plain SSR, …); drop this plugin into your
 * vite.config and deploy.
 *
 * Archive layout:
 *   manifest.json   { version, entry, server[], client[] }
 *   client/**       static assets served directly
 *   server/**       ESM modules loaded into the hosting isolate
 */

const OUTPUT_ZIP = "whop-build.zip";
const ALWAYS_IGNORED = new Set([".DS_Store", ".assetsignore"]);
// The @cloudflare/vite-plugin deploy descriptor — not an app module.
const SERVER_IGNORED = new Set(["wrangler.json"]);

/**
 * Dotfiles are config/secrets (.dev.vars, .env), never deployable output — a
 * real user's build shipped .dev.vars into their archive. Matched on the file's
 * own name, so `.well-known/…` assets still ship.
 */
function isDotfile(name: string): boolean {
	return name.startsWith(".");
}

function isServerIgnored(name: string): boolean {
	return SERVER_IGNORED.has(name) || isDotfile(name);
}

type Manifest = {
	version: 1;
	entry: string | null;
	server: string[];
	client: string[];
};

function walk(dir: string): string[] {
	const files: string[] = [];
	for (const entry of readdirSync(dir)) {
		const full = join(dir, entry);
		if (statSync(full).isDirectory()) {
			if (entry === ".vite") continue;
			files.push(...walk(full));
		} else {
			files.push(full);
		}
	}
	return files;
}

function loadIgnored(dir: string): Set<string> {
	const ignoreFile = join(dir, ".assetsignore");
	if (!existsSync(ignoreFile)) return new Set();
	return new Set(
		readFileSync(ignoreFile, "utf-8")
			.split("\n")
			.map((line) => line.trim())
			.filter((line) => line && !line.startsWith("#")),
	);
}

function collect(dir: string, isIgnored: (name: string) => boolean): string[] {
	if (!existsSync(dir)) return [];
	return (
		walk(dir)
			.filter((file) => !ALWAYS_IGNORED.has(basename(file)))
			.filter((file) => !isIgnored(basename(file)))
			// Archive keys are always forward-slash, even on Windows.
			.map((file) => relative(dir, file).split(sep).join("/"))
			.sort()
	);
}

function pack(distDir: string): void {
	const clientDir = join(distDir, "client");
	const serverDir = join(distDir, "server");

	const clientIgnored = loadIgnored(clientDir);
	// The client half is served publicly, so it needs the same dotfile guard as
	// the server half: Vite copies `public/` into dist/client verbatim, so a
	// `.env` left there would otherwise be packed and fetchable.
	const clientSecrets: string[] = [];
	const client = collect(clientDir, (name) => {
		if (clientIgnored.has(name)) return true;
		if (!isDotfile(name)) return false;
		clientSecrets.push(name);
		return true;
	});
	const server = collect(serverDir, isServerIgnored);
	if (clientSecrets.length > 0) {
		// Say so rather than dropping silently — the file was in the build output,
		// and "my asset didn't deploy" is worth one line to explain.
		const names = [...new Set(clientSecrets)].sort().join(", ");
		console.warn(
			`[whop] skipped ${clientSecrets.length} dotfile(s) in ${clientDir} (${names}) — client files are served publicly, so dotfiles are never packed.`,
		);
	}
	if (client.length === 0 && server.length === 0) {
		// A previous archive must not survive an empty build — `whop apps deploy`
		// would silently ship the stale zip.
		rmSync(join(distDir, OUTPUT_ZIP), { force: true });
		console.warn(
			`[whop] no build output under ${clientDir} or ${serverDir} — skipped packing and removed any previous dist/${OUTPUT_ZIP}.`,
		);
		return;
	}

	const entry = server.includes("index.js") ? "index.js" : null;
	if (server.length > 0 && !entry) {
		throw new Error(
			`[whop] server build in ${serverDir} has no index.js entry module.`,
		);
	}

	const manifest: Manifest = { version: 1, entry, server, client };
	const contents: Record<string, Uint8Array> = {
		"manifest.json": new TextEncoder().encode(JSON.stringify(manifest)),
	};
	for (const path of client)
		contents[`client/${path}`] = readFileSync(join(clientDir, path));
	for (const path of server)
		contents[`server/${path}`] = readFileSync(join(serverDir, path));

	const zip = zipSync(contents);
	writeFileSync(join(distDir, OUTPUT_ZIP), zip);
	const checksum = createHash("sha256").update(zip).digest("hex");
	console.log(
		`[whop] packed ${client.length + server.length} files → dist/${OUTPUT_ZIP} (sha256 ${checksum.slice(0, 12)}…)`,
	);
}

export type WhopOptions = {
	/**
	 * Resolve `@tanstack/*-devtools` to a stub, so the floating devtools panel
	 * never renders.
	 *
	 * Set by `whop apps init` on the projects Whop builds, where the page is
	 * watched by the person the site is being built for and a developer panel
	 * belongs to nobody. Delete it — or set it to false — to get them back.
	 *
	 * Deliberately a flag in the project rather than something inferred from the
	 * environment: whoever wonders where the devtools went finds the answer in the
	 * config they are already reading, and turning them on is one word.
	 */
	disableTanstackDevtools?: boolean;
};

/** The devtools packages `disableTanstackDevtools` points at a stub. */
const DEVTOOLS_PACKAGES = [
	"@tanstack/react-devtools",
	"@tanstack/react-router-devtools",
	"@tanstack/react-query-devtools",
];

function devtoolsAliases(): Record<string, string> {
	const stub = fileURLToPath(
		new URL("./vite-devtools-stub.js", import.meta.url),
	);
	return Object.fromEntries(DEVTOOLS_PACKAGES.map((name) => [name, stub]));
}

/**
 * Serve-mode wiring for `whop apps dev --port/--host/--public-host`.
 *
 * A dev server that is reached through a proxy needs three things it cannot
 * work out for itself: permission to answer for the proxy's hostname, a bind
 * address other than loopback, and an HMR socket URL pointing at the proxy
 * rather than at its own private port.
 */
function whopDevServer(options: WhopOptions): Plugin {
	return {
		name: "whop-dev-server",
		apply: "serve",
		config() {
			const port = Number(process.env.WHOP_DEV_PORT);
			const publicOrigin = process.env.WHOP_DEV_PUBLIC_ORIGIN;

			const server: NonNullable<UserConfig["server"]> = {};
			const hmr: HmrOptions = {};

			if (Number.isFinite(port) && port > 0) {
				server.port = port;
				// The port is derived from the app id and the proxy routes to exactly
				// it, so silently landing on port+1 would serve nothing.
				server.strictPort = true;
			}
			if (process.env.WHOP_DEV_HOST) server.host = process.env.WHOP_DEV_HOST;

			if (publicOrigin) {
				let parsed: URL | undefined;
				try {
					parsed = new URL(publicOrigin);
				} catch {
					parsed = undefined;
				}
				if (parsed) {
					server.allowedHosts = [parsed.hostname];

					const connection = {
						protocol: parsed.protocol === "https:" ? "wss" : "ws",
						host: parsed.hostname,
						clientPort: parsed.port
							? Number(parsed.port)
							: parsed.protocol === "https:"
								? 443
								: 80,
					};

					// Vite 8 moved these to `server.ws` and deprecated them on `hmr`;
					// on Vite 7 `server.ws` is a boolean that turns the websocket server
					// off altogether, so writing an object there would break HMR rather
					// than configure it. Projects scaffolded now are on 8, and one pulled
					// from an older build can still be on 7.
					if (Number.parseInt(viteVersion, 10) >= 8) {
						server.ws = connection;
					} else {
						Object.assign(hmr, connection);
					}

					// `overlay` stayed on `hmr` in both. A site reached through
					// `--public_host` is being watched by the person it is being built
					// for, not by its developer: a syntax error mid-edit is normal and
					// usually fixed by the next one, so the stack trace is for the logs.
					hmr.overlay = false;
					server.hmr = hmr;
				}
			}

			const resolve = options.disableTanstackDevtools
				? { alias: devtoolsAliases() }
				: undefined;

			return {
				...(Object.keys(server).length > 0 ? { server } : {}),
				...(resolve ? { resolve } : {}),
			};
		},
	};
}

function whopPack(): Plugin {
	let distDir = "dist";
	return {
		name: "whop-hosting",
		apply: "build",
		configResolved(config) {
			// build.outDir is per-environment (dist/client, dist/server); the archive
			// lives at their common parent so it survives each environment's build.
			distDir = config.build.outDir.replace(/[/\\](client|server)\/?$/, "");
		},
		// Runs after each environment's build; the final one sees both client and
		// server on disk and writes the authoritative archive.
		closeBundle() {
			pack(distDir);
		},
	};
}

export function whop(options: WhopOptions = {}): Plugin[] {
	return [whopDevServer(options), whopPack()];
}

export default whop;
